// libs
import React, { memo, useEffect, useState } from 'react';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// styles
import { Button, Container, Content, ExpanderIconWrapper } from './styles';

// constants
import { TAB_INDEX } from 'shared/constants';

// defs
interface ICollapsibleProps {
  header: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
  childrenClassName?: string;
  onChange?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  CustomExpander?: ({ isExpanded }: { isExpanded: boolean; }) => React.ReactNode;
  fixedContentHeight?: boolean;
  onTransitionEnd?: VoidFunction;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

function Collapsible({
  header,
  children,
  className,
  headerClassName = '',
  childrenClassName = '',
  onChange,
  isExpanded = false,
  CustomExpander,
  onTransitionEnd = EMPTY_FUNC,
}: ICollapsibleProps) {
  const PlusIcon = useStoreIcon(IconName.PLUS);

  const [showContent, setShowContent] = useState(isExpanded);

  const expanderCLName = `cp-expander ${showContent ? 'expanded' : ''}`;
  const headerCLName = `cp-header ${headerClassName}`;
  const contentCLName = `cp-content ${childrenClassName} ${!showContent ? 'collapsed' : ''}`;

  const onClick = () => {
    const newShowContent = !showContent;
    setShowContent(newShowContent);
    onChange?.(newShowContent);
  };

  useEffect(() => {
    setShowContent(isExpanded);
  }, [isExpanded]);

  return (
    <Container className={className} data-test="wrapperContent">
      <Button
        className={headerCLName}
        onClick={onClick}
        aria-expanded={showContent}
        data-test="headerContent"
        tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
      >
        {header}
        {CustomExpander ? (
          <CustomExpander isExpanded={showContent} data-test="custom-expander" />
        ) : (
          <ExpanderIconWrapper className={expanderCLName} data-test="default-expander">
            <PlusIcon />
          </ExpanderIconWrapper>
        )}
      </Button>

      <Content
        style={{ height: showContent ? 'auto' : 0, visibility: showContent ? 'visible' : 'hidden' }}
        className={contentCLName}
        data-test="childContent"
        onTransitionEnd={onTransitionEnd}
      >
        {children}
      </Content>
    </Container>
  );
}

export default memo(Collapsible);
